import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FloorGroup, FloorLabel } from '../../types'
import { dispatchCatalogEvent, FLOOR_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { replaceCachedPlacementsForSpace } from './floorPlacements'
import { stripUndefined } from './stripUndefined'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'
import type { FloorSpaceLayout } from '../floorLayoutGeometry'

const groupsCacheByPlace: Record<string, FloorGroup[]> = {}
const labelsCacheByPlace: Record<string, FloorLabel[]> = {}
let groupsUnsubscribe: (() => void) | null = null
let labelsUnsubscribe: (() => void) | null = null

function dispatchFloorUpdated(): void {
  dispatchCatalogEvent(FLOOR_UPDATED_EVENT)
}

function groupsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'floorGroups')
}

function groupDoc(placeId: string, groupId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'floorGroups', groupId)
}

function labelsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'floorLabels')
}

function labelDoc(placeId: string, labelId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'floorLabels', labelId)
}

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function docToGroup(d: QueryDocumentSnapshot): FloorGroup {
  const data = d.data()
  return {
    id: d.id,
    spaceId: typeof data.spaceId === 'string' ? data.spaceId : '',
    x: num(data.x),
    y: num(data.y),
    w: Math.max(1, num(data.w, 360)),
    h: Math.max(1, num(data.h, 280)),
    title: typeof data.title === 'string' && data.title.trim() ? data.title : 'Group',
    color: typeof data.color === 'string' && data.color.trim() ? data.color.trim() : '#6366f1',
  }
}

function docToLabel(d: QueryDocumentSnapshot): FloorLabel {
  const data = d.data()
  return {
    id: d.id,
    spaceId: typeof data.spaceId === 'string' ? data.spaceId : '',
    x: num(data.x),
    y: num(data.y),
    w: Math.max(1, num(data.w, 180)),
    text: typeof data.text === 'string' ? data.text : '',
    fontSize: typeof data.fontSize === 'number' && Number.isFinite(data.fontSize) ? data.fontSize : undefined,
    color: typeof data.color === 'string' && data.color.trim() ? data.color.trim() : undefined,
    groupId:
      typeof data.groupId === 'string' && data.groupId.trim() ? data.groupId.trim() : undefined,
  }
}

export async function primeFloorObjectsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (groupsUnsubscribe) {
    groupsUnsubscribe()
    groupsUnsubscribe = null
  }
  if (labelsUnsubscribe) {
    labelsUnsubscribe()
    labelsUnsubscribe = null
  }
  const [groupSnap, labelSnap] = await Promise.all([
    getDocs(groupsRef(placeId)),
    getDocs(labelsRef(placeId)),
  ])
  groupsCacheByPlace[placeId] = groupSnap.docs.map(docToGroup)
  labelsCacheByPlace[placeId] = labelSnap.docs.map(docToLabel)
  groupsUnsubscribe = onSnapshot(groupsRef(placeId), (snapshot) => {
    groupsCacheByPlace[placeId] = snapshot.docs.map(docToGroup)
    dispatchFloorUpdated()
  })
  labelsUnsubscribe = onSnapshot(labelsRef(placeId), (snapshot) => {
    labelsCacheByPlace[placeId] = snapshot.docs.map(docToLabel)
    dispatchFloorUpdated()
  })
}

export function getFloorGroups(): FloorGroup[] {
  const placeId = getCurrentPlaceId()
  return groupsCacheByPlace[placeId] ?? []
}

export function getFloorLabels(): FloorLabel[] {
  const placeId = getCurrentPlaceId()
  return labelsCacheByPlace[placeId] ?? []
}

function replaceCachedGroupsForSpace(spaceId: string, next: FloorGroup[]): BatchWriteOp[] {
  const placeId = getCurrentPlaceId()
  const cache = groupsCacheByPlace[placeId] ?? []
  const existing = cache.filter((g) => g.spaceId === spaceId)
  const keep = cache.filter((g) => g.spaceId !== spaceId)
  const normalized = next.map((g) => ({ ...g, spaceId }))
  groupsCacheByPlace[placeId] = [...keep, ...normalized]
  const nextIds = new Set(normalized.map((g) => g.id))
  const ops: BatchWriteOp[] = []
  for (const g of existing) {
    if (!nextIds.has(g.id)) ops.push({ type: 'delete', ref: groupDoc(placeId, g.id) })
  }
  for (const g of normalized) {
    ops.push({
      type: 'set',
      ref: groupDoc(placeId, g.id),
      data: stripUndefined(g) as object,
    })
  }
  return ops
}

function replaceCachedLabelsForSpace(spaceId: string, next: FloorLabel[]): BatchWriteOp[] {
  const placeId = getCurrentPlaceId()
  const cache = labelsCacheByPlace[placeId] ?? []
  const existing = cache.filter((l) => l.spaceId === spaceId)
  const keep = cache.filter((l) => l.spaceId !== spaceId)
  const normalized = next.map((l) => ({ ...l, spaceId }))
  labelsCacheByPlace[placeId] = [...keep, ...normalized]
  const nextIds = new Set(normalized.map((l) => l.id))
  const ops: BatchWriteOp[] = []
  for (const l of existing) {
    if (!nextIds.has(l.id)) ops.push({ type: 'delete', ref: labelDoc(placeId, l.id) })
  }
  for (const l of normalized) {
    ops.push({
      type: 'set',
      ref: labelDoc(placeId, l.id),
      data: stripUndefined(l) as object,
    })
  }
  return ops
}

export function saveFloorSpaceLayout(spaceId: string, layout: FloorSpaceLayout): void {
  const ops = [
    ...replaceCachedPlacementsForSpace(spaceId, layout.placements),
    ...replaceCachedGroupsForSpace(spaceId, layout.groups),
    ...replaceCachedLabelsForSpace(spaceId, layout.labels),
  ]
  dispatchFloorUpdated()
  commitWriteOps(ops).catch((e) => console.error('Firestore saveFloorSpaceLayout failed', e))
}

export function deleteFloorObjectsForSpace(spaceId: string): void {
  const placeId = getCurrentPlaceId()
  const groups = (groupsCacheByPlace[placeId] ?? []).filter((g) => g.spaceId === spaceId)
  const labels = (labelsCacheByPlace[placeId] ?? []).filter((l) => l.spaceId === spaceId)
  groupsCacheByPlace[placeId] = (groupsCacheByPlace[placeId] ?? []).filter((g) => g.spaceId !== spaceId)
  labelsCacheByPlace[placeId] = (labelsCacheByPlace[placeId] ?? []).filter((l) => l.spaceId !== spaceId)
  const ops: BatchWriteOp[] = [
    ...groups.map((g) => ({ type: 'delete' as const, ref: groupDoc(placeId, g.id) })),
    ...labels.map((l) => ({ type: 'delete' as const, ref: labelDoc(placeId, l.id) })),
  ]
  dispatchFloorUpdated()
  commitWriteOps(ops).catch((e) => console.error('Firestore deleteFloorObjectsForSpace failed', e))
}

export function clearFloorObjectsCache(): void {
  if (groupsUnsubscribe) {
    groupsUnsubscribe()
    groupsUnsubscribe = null
  }
  if (labelsUnsubscribe) {
    labelsUnsubscribe()
    labelsUnsubscribe = null
  }
  Object.keys(groupsCacheByPlace).forEach((k) => delete groupsCacheByPlace[k])
  Object.keys(labelsCacheByPlace).forEach((k) => delete labelsCacheByPlace[k])
}
